import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Download, Check, AlertTriangle, Terminal, Copy, Rocket, Clock, CalendarDays, Building2, Monitor, ChevronDown } from 'lucide-react';
import api from '@/lib/api';

const SERVER_VERSION_LABEL = 'v3.0.0';

function parseSemver(v) {
  const parts = (v || '').replace(/^v/, '').split('.');
  return parts.map(p => parseInt(p, 10) || 0);
}

function semverGte(a, b) {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return true;
}

function ToolLed({ running }) {
  if (running === true) return <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" title="Aktiv" />;
  if (running === false) return <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500" title="Gestoppt" />;
  return <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-600" title="Unbekannt" />;
}

function InstallLed({ status, timestamp }) {
  if (!status || status === 'success') return null;
  const dt = timestamp ? new Date(timestamp) : null;
  const label = dt ? dt.toLocaleTimeString('de-DE', {hour:'2-digit',minute:'2-digit',second:'2-digit'}) : '';
  const statusMap = {
    'installing': { color: 'bg-yellow-500 shadow-[0_0_12px_#eab308]', text: 'text-yellow-400', label: 'Agent...' },
    'installing_tv': { color: 'bg-yellow-500 shadow-[0_0_12px_#eab308]', text: 'text-yellow-400', label: 'TeamViewer...' },
    'installing_rd': { color: 'bg-yellow-500 shadow-[0_0_12px_#eab308]', text: 'text-yellow-400', label: 'RustDesk...' },
    'installing_ad': { color: 'bg-yellow-500 shadow-[0_0_12px_#eab308]', text: 'text-yellow-400', label: 'AnyDesk...' },
    'partial': { color: 'bg-orange-500 shadow-[0_0_8px_#f97316]', text: 'text-orange-400', label: 'TEILWEISE' },
    'failed': { color: 'bg-red-500 shadow-[0_0_8px_#ef4444]', text: 'text-red-400', label: 'FEHLER' },
  };
  const s = statusMap[status] || statusMap['installing'];
  return (
    <div className="flex items-center gap-2" title={`${s.label} ${label}`}>
      <span className={`inline-block w-3 h-3 rounded-full ${s.color} animate-pulse`} />
      <span className={`text-[10px] font-bold ${s.text}`}>{s.label} {label}</span>
    </div>
  );
}

function generateCmdCommand(tenantCode) {
  const tc = tenantCode || 'tsr';
  // Two-step approach: 
  // Step 1) Download script as current user (anyone can write to ProgramData)
  // Step 2) powershell Start-Process with -Verb RunAs triggers UAC prompt -> elevated shell runs install
  const url = 'https://remote.tsrsystems.com/api/agent/v1/install-update?tenant=' + tc + '&device=%COMPUTERNAME%';
  const dlPart = 'mkdir "C:\\ProgramData\\TSR-Remote" 2>nul & curl -s -o "C:\\ProgramData\\TSR-Remote\\tsr-install.ps1" "' + url + '"';
  // The elevated part: powershell Start-Process with -Verb RunAs -> UAC prompt, then runs ps1 as admin
  // Start elevated CMD with /k (keeps window open ALWAYS, even on crash); inside CMD we run powershell -File. 
  // /k PRINTS the command and stays open for user to read output + errors.
  const runPart = 'powershell -NoProfile -ExecutionPolicy Bypass -Command "Start-Process cmd.exe -Verb RunAs -ArgumentList \'/k\',\'powershell -NoProfile -ExecutionPolicy Bypass -File C:\\ProgramData\\TSR-Remote\\tsr-install.ps1\'"';
  return dlPart + ' & ' + runPart;
}

// === Compact Install-Status Table ===
const INSTALL_STEPS = [
  { num: 1,  name: 'Verzeichnis',    key: 'installing',     short: 'Dir', full: 'Verzeichnis erstellen' },
  { num: 2,  name: 'Agent-Download', key: 'agent_download', short: 'DL',  full: 'Agent herunterladen' },
  { num: 3,  name: 'Alte stoppen',   key: 'stop_old',       short: 'Stp', full: 'Alte Instanzen stoppen' },
  { num: 4,  name: 'Task',           key: 'task',           short: 'Tsk', full: 'Scheduled Task registrieren' },
  { num: 5,  name: 'Agent-Start',    key: 'agent_start',    short: 'Run', full: 'Agent starten' },
  { num: 6,  name: 'TeamViewer',     key: 'installing_tv',  short: 'TV',  full: 'TeamViewer' },
  { num: 7,  name: 'RustDesk',       key: 'installing_rd',  short: 'RD',  full: 'RustDesk' },
  { num: 8,  name: 'AnyDesk',        key: 'installing_ad',  short: 'AD',  full: 'AnyDesk' },
  { num: 9,  name: 'TSRID',          key: 'tsrid',          short: 'TSR', full: 'TSRID Software' },
  { num: 10, name: 'Resources',      key: 'resources',      short: 'Res', full: 'Resources-Dokumente' },
  { num: 11, name: 'Kiosk',          key: 'kiosk',          short: 'Kio', full: 'Kiosk-Modus' },
  { num: 12, name: 'Regula-Lizenz',  key: 'regula_lic',     short: 'Lic', full: 'Regula devices.dat (Lizenz)' },
];

function computeStepStates(device) {
  // Returns array of {state: 'ok'|'fail'|'current'|'pending'|'skip', detail} for each of 11 steps
  const steps = device.install_steps || [];
  const status = device.install_status;
  const errorText = (device.install_error || '').toLowerCase();
  const isDone = status === 'success';
  const isFailed = status === 'failed';
  const isPartial = status === 'partial';

  const byNum = {};
  const byKey = {};
  for (const s of steps) {
    if (s.step_num) byNum[s.step_num] = s;
    if (s.status) byKey[s.status] = s;
  }

  // Find current step index by the latest status starting with 'installing'
  const lastStatus = steps.length ? steps[steps.length - 1].status : status;
  const currentKeyIdx = INSTALL_STEPS.findIndex(x => x.key === lastStatus);

  return INSTALL_STEPS.map((def, idx) => {
    const explicit = byNum[def.num];
    if (explicit) {
      if (explicit.step_status === 'fail') return { state: 'fail', detail: explicit.error || def.full };
      if (explicit.step_status === 'warn') return { state: 'warn', detail: explicit.error || def.full };
      return { state: 'ok', detail: def.full };
    }
    // Fallback via status key match
    if (byKey[def.key]) return { state: 'ok', detail: def.full };
    if (isDone) return { state: 'ok', detail: def.full };
    if (isPartial) {
      // Kiosk failed heuristic
      if (def.num === 11 && errorText.includes('kiosk')) return { state: 'fail', detail: device.install_error };
      if (def.num === 11 && errorText.includes('hklm')) return { state: 'fail', detail: device.install_error };
      return { state: 'ok', detail: def.full };
    }
    if (isFailed && currentKeyIdx >= 0 && idx === currentKeyIdx) return { state: 'fail', detail: device.install_error || def.full };
    if (currentKeyIdx >= 0 && idx === currentKeyIdx) return { state: 'current', detail: def.full };
    if (currentKeyIdx >= 0 && idx < currentKeyIdx) return { state: 'ok', detail: def.full };
    return { state: 'pending', detail: def.full };
  });
}

function StepCell({ state, detail, def }) {
  const conf = {
    ok:      { cls: 'bg-green-900/40 text-green-400 border-green-600/40',   ch: '✓' },
    fail:    { cls: 'bg-red-900/50  text-red-300   border-red-600/50 animate-pulse', ch: '✗' },
    warn:    { cls: 'bg-orange-900/40 text-orange-400 border-orange-600/40', ch: '!' },
    current: { cls: 'bg-yellow-900/40 text-yellow-300 border-yellow-600/50 animate-pulse', ch: '…' },
    pending: { cls: 'bg-[#252525] text-[#555] border-[#3A3A3A]',             ch: '·' },
    skip:    { cls: 'bg-[#1f1f1f] text-[#444] border-[#2A2A2A]',             ch: '-' },
  }[state] || { cls: 'bg-[#252525] text-[#555] border-[#3A3A3A]', ch: '·' };
  return (
    <div
      className={`w-6 h-6 flex items-center justify-center rounded text-[10px] font-bold border ${conf.cls}`}
      title={`${def.num}. ${def.full}${detail && detail !== def.full ? ': ' + detail : ''}`}
    >
      {conf.ch}
    </div>
  );
}

function InstallStatusTable({ devices, onDismiss, navigate }) {
  const [sortBy, setSortBy] = useState('priority');
  const [sortDir, setSortDir] = useState('asc');
  const [filter, setFilter] = useState('with-status'); // all | with-status | failed | active | success
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const list = devices;

  const stats = {
    total: list.length,
    withStatus: list.filter(d => d.install_status).length,
    success: list.filter(d => d.install_status === 'success').length,
    active: list.filter(d => d.install_status && d.install_status.startsWith('installing')).length,
    failed: list.filter(d => d.install_status === 'failed').length,
    partial: list.filter(d => d.install_status === 'partial').length,
    noStatus: list.filter(d => !d.install_status).length,
  };

  const statusPriority = s =>
    s === 'failed' ? 0 :
    s === 'partial' ? 1 :
    s && s.startsWith('installing') ? 2 :
    s === 'success' ? 3 : 4;

  let filtered = list;
  if (filter === 'with-status') filtered = filtered.filter(d => d.install_status);
  if (filter === 'failed') filtered = filtered.filter(d => ['failed', 'partial'].includes(d.install_status));
  if (filter === 'active') filtered = filtered.filter(d => d.install_status && d.install_status.startsWith('installing'));
  if (filter === 'success') filtered = filtered.filter(d => d.install_status === 'success');
  if (filter === 'no-status') filtered = filtered.filter(d => !d.install_status);
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(d =>
      (d.computer_name || '').toLowerCase().includes(q) ||
      (d.install_error || '').toLowerCase().includes(q) ||
      (d.tenant_code || '').toLowerCase().includes(q)
    );
  }

  filtered = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'priority') cmp = statusPriority(a.install_status) - statusPriority(b.install_status);
    else if (sortBy === 'name') cmp = (a.computer_name || '').localeCompare(b.computer_name || '');
    else if (sortBy === 'time') cmp = (a.install_at || '').localeCompare(b.install_at || '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  return (
    <div data-testid="install-status-table" className="bg-[#252525] rounded-xl border border-[#3A3A3A] overflow-hidden">
      {/* Header bar */}
      <div className="px-4 py-2.5 border-b border-[#3A3A3A] bg-[#1f1f1f] flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 shadow-[0_0_8px_#eab308]" />
          <h3 className="text-sm font-bold text-white">Install-Status</h3>
          <span className="text-[11px] text-[#888]">· {stats.total} Geräte</span>
        </div>
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          <FilterChip active={filter === 'with-status'} onClick={() => setFilter('with-status')} count={stats.withStatus} label="Mit Status" />
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} count={stats.total} label="Alle" />
          {stats.active > 0 && <FilterChip active={filter === 'active'} onClick={() => setFilter('active')} count={stats.active} label="Aktiv" color="yellow" />}
          {(stats.failed + stats.partial) > 0 && <FilterChip active={filter === 'failed'} onClick={() => setFilter('failed')} count={stats.failed + stats.partial} label="Fehler" color="red" />}
          <FilterChip active={filter === 'success'} onClick={() => setFilter('success')} count={stats.success} label="OK" color="green" />
          {stats.noStatus > 0 && <FilterChip active={filter === 'no-status'} onClick={() => setFilter('no-status')} count={stats.noStatus} label="Unbekannt" color="gray" />}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filtern: Hostname, Fehler, Mandant..."
          className="text-xs bg-[#1a1a1a] border border-[#3A3A3A] rounded px-2 py-1 text-white w-48 placeholder:text-[#555]"
          data-testid="install-status-search"
        />
      </div>

      {/* Column legend removed - numbers now show directly above each step cell */}

      {/* Table */}
      <div className="max-h-[600px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#1a1a1a] sticky top-0 z-10">
            <tr className="text-left text-[10px] uppercase text-[#888] border-b border-[#3A3A3A]">
              <th className="px-2 py-2 w-10">#</th>
              <th className="px-2 py-2 cursor-pointer hover:text-white whitespace-nowrap" onClick={() => toggleSort('name')}>
                Gerät {sortBy === 'name' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              <th className="px-2 py-2 cursor-pointer hover:text-white" onClick={() => toggleSort('priority')}>
                Status {sortBy === 'priority' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              {INSTALL_STEPS.map(s => (
                <th key={s.num} className="px-1 py-2 text-center" title={`${s.num}. ${s.full}`} style={{ minWidth: '68px' }}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[11px] font-bold text-white">{s.num}</span>
                    <span className="text-[9px] font-normal text-[#aaa] normal-case leading-tight whitespace-nowrap">{s.name}</span>
                  </div>
                </th>
              ))}
              <th className="px-2 py-2">Fehler</th>
              <th className="px-2 py-2 cursor-pointer hover:text-white whitespace-nowrap" onClick={() => toggleSort('time')}>
                Zeit {sortBy === 'time' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              <th className="px-2 py-2 text-center w-20">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={17} className="text-center text-[#666] py-8">Keine Einträge</td></tr>
            )}
            {filtered.map((d, idx) => {
              const stepStates = computeStepStates(d);
              const dt = d.install_at ? new Date(d.install_at) : null;
              const timeLabel = dt ? dt.toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit', second:'2-digit'}) : '';
              const isExpanded = expandedId === d.id;
              const s = d.install_status;
              const statusBadge =
                s === 'success' ? <span className="text-[10px] font-bold text-green-400 bg-green-900/30 px-2 py-0.5 rounded">OK</span> :
                s === 'failed' ? <span className="text-[10px] font-bold text-red-400 bg-red-900/30 px-2 py-0.5 rounded animate-pulse">FEHLER</span> :
                s === 'partial' ? <span className="text-[10px] font-bold text-orange-400 bg-orange-900/30 px-2 py-0.5 rounded">TEILW.</span> :
                s && s.startsWith('installing') ? <span className="text-[10px] font-bold text-yellow-400 bg-yellow-900/30 px-2 py-0.5 rounded animate-pulse">LÄUFT</span> :
                <span className="text-[10px] text-[#888]">{s}</span>;
              return (
                <>
                  <tr
                    key={d.id}
                    className={`border-t border-[#2A2A2A] hover:bg-[#2A2A2A] cursor-pointer ${isExpanded ? 'bg-[#2A2A2A]' : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : d.id)}
                    data-testid={`install-row-${d.computer_name}`}
                  >
                    <td className="px-3 py-2 text-[#666] font-mono">{idx + 1}</td>
                    <td className="px-3 py-2 text-white font-semibold">{d.computer_name}</td>
                    <td className="px-2 py-2">{statusBadge}</td>
                    {stepStates.map((st, i) => (
                      <td key={i} className="px-0 py-2 text-center align-middle">
                        <div className="flex justify-center">
                          <StepCell state={st.state} detail={st.detail} def={INSTALL_STEPS[i]} />
                        </div>
                      </td>
                    ))}
                    <td className="px-2 py-2 text-[10px] text-orange-400 max-w-[220px] truncate" title={d.install_error}>
                      {d.install_error || (s === 'success' ? <span className="text-[#555]">—</span> : '')}
                    </td>
                    <td className="px-2 py-2 text-[10px] text-[#888] font-mono whitespace-nowrap">{timeLabel}</td>
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate && navigate(`/devices/${d.id}`)}
                        data-testid={`install-details-${d.computer_name}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border border-[#3A3A3A] text-[#aaa] hover:text-white hover:border-[#D50C2D]/50 hover:bg-[#D50C2D]/10 transition-colors"
                        title="Geräte-Details öffnen"
                      >
                        Details →
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-[#1a1a1a]">
                      <td colSpan={17} className="px-6 py-3 border-t border-[#2A2A2A]">
                        <div className="space-y-2">
                          {d.install_error && (
                            <div className="text-[11px] font-mono text-red-300 bg-red-900/10 border border-red-900/30 rounded p-2 whitespace-pre-wrap">
                              <div className="text-red-400 font-bold mb-1">Fehlermeldung:</div>
                              {d.install_error}
                            </div>
                          )}
                          {d.install_steps && d.install_steps.length > 0 && (
                            <div className="text-[11px]">
                              <div className="text-[#888] font-bold mb-1">Historie ({d.install_steps.length} Einträge):</div>
                              <div className="space-y-0.5 max-h-40 overflow-y-auto font-mono">
                                {d.install_steps.slice().reverse().map((step, i) => (
                                  <div key={i} className="flex items-start gap-2 text-[10px]">
                                    <span className="text-[#555] whitespace-nowrap">{step.at ? new Date(step.at).toLocaleTimeString('de-DE') : ''}</span>
                                    <span className={`font-semibold ${step.step_status === 'fail' ? 'text-red-400' : step.step_status === 'ok' ? 'text-green-400' : 'text-yellow-400'}`}>
                                      {step.step_num ? `[${step.step_num}]` : ''} {step.step_name || step.status}
                                    </span>
                                    {step.error && <span className="text-red-400">— {step.error}</span>}
                                    {step.tv_id && <span className="text-blue-400">TV: {step.tv_id}</span>}
                                    {step.rd_id && <span className="text-cyan-400">RD: {step.rd_id}</span>}
                                    {step.ad_id && <span className="text-purple-400">AD: {step.ad_id}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, count, label, color = 'gray' }) {
  const colors = {
    gray: active ? 'bg-white/10 text-white border-white/20' : 'bg-[#2A2A2A] text-[#888] border-[#3A3A3A] hover:text-white',
    red: active ? 'bg-red-900/40 text-red-300 border-red-600/40' : 'bg-[#2A2A2A] text-red-400 border-[#3A3A3A] hover:bg-red-900/20',
    yellow: active ? 'bg-yellow-900/40 text-yellow-300 border-yellow-600/40' : 'bg-[#2A2A2A] text-yellow-400 border-[#3A3A3A] hover:bg-yellow-900/20',
    green: active ? 'bg-green-900/40 text-green-300 border-green-600/40' : 'bg-[#2A2A2A] text-green-400 border-[#3A3A3A] hover:bg-green-900/20',
  };
  return (
    <button onClick={onClick} className={`text-[10px] font-bold px-2.5 py-1 rounded border transition-colors ${colors[color] || colors.gray}`}>
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}

export default function AgentUpdatePage() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [activeTab, setActiveTab] = useState('cmd');
  const [loading, setLoading] = useState(true);
  const [rolloutLoading, setRolloutLoading] = useState(false);
  const [rolloutHistory, setRolloutHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [serverVersion, setServerVersion] = useState(SERVER_VERSION_LABEL);
  const [downloadUrl, setDownloadUrl] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantsRes, configRes] = await Promise.all([
        api.get('/agent/tenants'),
        api.get('/agent/config'),
      ]);
      setTenants(tenantsRes.data || []);
      const cfg = configRes.data || {};
      setServerVersion(cfg.server_version || SERVER_VERSION_LABEL);
      setDownloadUrl(cfg.download_url || '');

      const defaultTenant = (tenantsRes.data || [])[0]?.code || '';
      if (!selectedTenant && defaultTenant) {
        setSelectedTenant(defaultTenant);
      }
    } catch (err) {
      console.error('Failed to load agent data', err);
    }
    setLoading(false);
  }, [selectedTenant]);

  const fetchDevices = useCallback(async () => {
    if (!selectedTenant) return;
    try {
      const [devRes, statsRes] = await Promise.all([
        api.get(`/agent/devices?tenant_code=${selectedTenant}`),
        api.get(`/agent/devices/stats?tenant_code=${selectedTenant}`),
      ]);
      setDevices(devRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error('Failed to load devices', err);
    }
  }, [selectedTenant]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/agent/rollout/history');
      setRolloutHistory(res.data || []);
    } catch (err) {
      console.error('Failed to load history', err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDevices(); }, [fetchDevices]);
  useEffect(() => { if (activeTab === 'history') fetchHistory(); }, [activeTab, fetchHistory]);

  // Auto-refresh every 5s for real-time install feedback
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDevices();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const outdatedDevices = devices.filter(d => !semverGte(d.agent_version, serverVersion));

  const toggleDevice = (id) => {
    setSelectedDevices(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllOutdated = () => {
    setSelectedDevices(outdatedDevices.map(d => d.id));
  };

  const selectAll = () => {
    setSelectedDevices(devices.map(d => d.id));
  };

  const startRollout = async () => {
    if (selectedDevices.length === 0) return;
    setRolloutLoading(true);
    try {
      await api.post('/agent/rollout', { device_ids: selectedDevices });
      setSelectedDevices([]);
      fetchDevices();
    } catch (err) {
      console.error('Rollout failed', err);
    }
    setRolloutLoading(false);
  };

  const copyCmd = () => {
    const cmd = generateCmdCommand(selectedTenant);
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onlineCount = devices.filter(d => d.is_online).length;

  const versionColors = {
    'v3.0.0': '#22c55e',
    'v2.2.0': '#22c55e',
    'v2.1.0': '#f97316',
    'v2.0.0': '#f97316',
    'v1.1': '#ef4444',
    'v1.0': '#ef4444',
  };

  const getVersionColor = (v) => {
    if (versionColors[v]) return versionColors[v];
    return semverGte(v, serverVersion) ? '#22c55e' : '#f97316';
  };

  return (
    <div data-testid="agent-update-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#D50C2D]/20 flex items-center justify-center">
            <Download className="w-5 h-5 text-[#D50C2D]" />
          </div>
          <div>
            <h1 data-testid="page-title" className="text-2xl font-bold text-white">Agent Update Center</h1>
            <p className="text-sm text-[#888]">Firmware-Rollout und Versionsverwaltung</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="refresh-button"
            onClick={() => { fetchData(); fetchDevices(); }}
            className="p-2 rounded-lg text-[#888] hover:text-white hover:bg-[#2A2A2A] transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <div data-testid="server-version-badge" className="px-3 py-1.5 rounded-lg bg-[#2A2A2A] border border-[#3A3A3A]">
            <span className="text-xs text-[#888]">Server: </span>
            <span className="text-sm font-bold text-[#D50C2D]">{serverVersion}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div data-testid="stats-cards" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#252525] rounded-xl p-6 border border-[#3A3A3A]">
            <div className="text-4xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-[#888] mt-1">Geraete gesamt</div>
          </div>
          <div className="bg-[#1a3a1a] rounded-xl p-6 border border-[#2a5a2a]">
            <div className="text-4xl font-bold text-white">{stats.current}</div>
            <div className="text-sm text-green-300/70 mt-1">Aktuell ({serverVersion})</div>
          </div>
          <div className="bg-[#3a2a1a] rounded-xl p-6 border border-[#5a3a2a]">
            <div className="text-4xl font-bold text-white">{stats.outdated}</div>
            <div className="text-sm text-orange-300/70 mt-1">Update noetig</div>
          </div>
        </div>
      )}

      {/* Live Install Activity Feed - Compact Table (always visible) */}
      <InstallStatusTable devices={devices} navigate={navigate} />

      {/* Version Distribution */}
      {stats && stats.version_distribution && (
        <div data-testid="version-distribution" className="bg-[#252525] rounded-xl p-5 border border-[#3A3A3A]">
          <h3 className="text-sm font-medium text-white mb-4">Versions-Verteilung</h3>
          <div className="space-y-3">
            {Object.entries(stats.version_distribution).map(([version, data]) => (
              <div key={version} className="flex items-center gap-4">
                <span className="text-sm font-mono w-16" style={{ color: getVersionColor(version) }}>{version}</span>
                <div className="flex-1 h-6 bg-[#1E1E1E] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.max(10, (data.total / stats.total) * 100)}%`,
                      backgroundColor: getVersionColor(version),
                      opacity: 0.7,
                    }}
                  />
                </div>
                <span className="text-xs text-[#888] w-40 text-right">
                  {data.total} Geraete ({data.online} online)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div data-testid="tabs" className="flex gap-1 border-b border-[#3A3A3A]">
        {[
          { id: 'cmd', label: 'CMD Generator', icon: Terminal },
          { id: 'update', label: 'Update starten', icon: Rocket },
          { id: 'history', label: 'Verlauf', icon: Clock },
          { id: 'schedule', label: 'Zeitplan', icon: CalendarDays },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === tab.id
                ? 'text-white border-[#D50C2D]'
                : 'text-[#888] border-transparent hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content: CMD Generator */}
      {activeTab === 'cmd' && (
        <div data-testid="cmd-generator-tab" className="space-y-5">
          {/* Tenant Selection */}
          <div className="bg-[#252525] rounded-xl p-5 border border-[#3A3A3A] space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-white">
              <Building2 className="w-5 h-5 text-[#D50C2D]" />
              Tenant
            </h3>
            <div className="relative">
              <select
                data-testid="cmd-tenant-select"
                value={selectedTenant}
                onChange={e => { setSelectedTenant(e.target.value); setSelectedDevices([]); }}
                className="w-full bg-[#2A2A2A] text-white border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#D50C2D]"
              >
                <option value="">Tenant waehlen...</option>
                {tenants.map(t => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888] pointer-events-none" />
            </div>
          </div>

          {selectedTenant && (
            <>
              {/* Info Box */}
              <div className="bg-[#1a2a3a] rounded-xl p-5 border border-[#2a4a6a] space-y-3">
                <h3 className="flex items-center gap-2 text-base font-semibold text-cyan-300">
                  <Terminal className="w-5 h-5" />
                  Agent v3.0 Installationsbefehl
                </h3>
                <p className="text-sm text-[#AAA]">
                  Diesen Befehl in <strong className="text-white">TeamViewer CMD</strong> einfuegen.
                  Er installiert den Agent v3.0 auf jedem Windows-Geraet — ohne schtasks, ohne UAC, ohne elevated CMD.
                </p>

                {/* Ablauf */}
                <div className="bg-[#0d1a2a] rounded-lg p-4 border border-[#1a3a5a] space-y-2">
                  <p className="text-xs font-semibold text-green-400 mb-2">Installations-Schritte (Live-Monitor im CMD):</p>
                  <div className="grid gap-1.5 text-xs">
                    {[
                      { num: '1', label: 'Download', desc: 'Script herunterladen + SYSTEM Task starten', color: 'bg-green-900/50 text-green-400' },
                      { num: '2', label: 'Verzeichnis', desc: 'C:\ProgramData\TSR-Remote erstellen', color: 'bg-green-900/50 text-green-400' },
                      { num: '3', label: 'Agent Download', desc: 'TSR-Remote-Service.ps1 herunterladen', color: 'bg-green-900/50 text-green-400' },
                      { num: '4', label: 'Alte Instanzen', desc: 'Vorherige Prozesse stoppen', color: 'bg-green-900/50 text-green-400' },
                      { num: '5', label: 'Scheduled Task', desc: 'Als SYSTEM registrieren + Watchdog', color: 'bg-green-900/50 text-green-400' },
                      { num: '6', label: 'TeamViewer', desc: 'Pruefen, installieren, Service starten, ID melden', color: 'bg-blue-900/50 text-blue-400' },
                      { num: '7', label: 'RustDesk', desc: 'Pruefen, installieren, Server-Konfig, ID melden', color: 'bg-blue-900/50 text-blue-400' },
                      { num: '8', label: 'AnyDesk', desc: 'Pruefen, installieren, Service starten, ID melden', color: 'bg-blue-900/50 text-blue-400' },
                      { num: '9', label: 'TSRID Ordner', desc: 'C:\tsrid\LATEST VERSION pruefen/erstellen/migrieren', color: 'bg-purple-900/50 text-purple-400' },
                      { num: '10', label: 'Dokumente', desc: 'doc1.pdf (DE) + doc2.pdf (GB) nach C:\RESOURCES aktualisieren', color: 'bg-purple-900/50 text-purple-400' },
                      { num: '11', label: 'Kiosk-Modus', desc: 'Shell-Ersatz (TSRID.exe) + Auto-Login (scan) + Lockdown', color: 'bg-red-900/50 text-red-400' },
                    ].map(s => (
                      <div key={s.num} className="flex items-start gap-2">
                        <span className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded text-center font-bold leading-5 ${s.color}`}>{s.num}</span>
                        <div>
                          <span className="text-white font-medium">{s.label}</span>
                          <span className="text-[#888] ml-2">{s.desc}</span>
                        </div>
                      </div>
                    ))}
                    <div className="mt-2 pt-2 border-t border-[#1a3a5a] text-[10px] text-green-400">
                      Echtzeit-Fortschritt wird im CMD-Fenster und auf der Agent Update Seite angezeigt.
                    </div>
                  </div>
                </div>
              </div>

              {/* Command Display */}
              <div className="bg-[#252525] rounded-xl border border-[#3A3A3A] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#3A3A3A]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="ml-2 text-xs text-[#888] font-mono">TeamViewer CMD</span>
                  </div>
                  <span className="text-xs text-[#555]">Tenant: {selectedTenant.toUpperCase()}</span>
                </div>
                <div data-testid="cmd-output" className="p-5 font-mono text-sm text-green-400 overflow-x-auto whitespace-pre-wrap break-all bg-[#0a0a0a] leading-relaxed">
                  {generateCmdCommand(selectedTenant)}
                </div>
              </div>

              {/* Copy Button */}
              <button
                data-testid="cmd-copy-btn"
                onClick={copyCmd}
                className="w-full flex items-center justify-center gap-2 px-5 py-4 rounded-xl text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 transition-all active:scale-[0.98]"
              >
                {copied ? (
                  <><Check className="w-5 h-5" /> Kopiert!</>
                ) : (
                  <><Copy className="w-5 h-5" /> Befehl in Zwischenablage kopieren</>
                )}
              </button>

              {/* Hinweis */}
              <div className="bg-[#2a2a1a] rounded-xl p-4 border border-[#4a4a2a] text-xs text-yellow-200/70">
                <strong className="text-yellow-300">Hinweis:</strong> Der Befehl funktioniert auf allen Windows-Geraeten.
                Er bereinigt automatisch alte Agenten (v1.1 + v2 Node.js), installiert Agent v3.0 und erstellt
                einen Scheduled Task mit Auto-Restart. Das Geraet erscheint nach ca. 30 Sekunden im Dashboard.
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab Content: Update starten */}
      {activeTab === 'update' && (
        <div className="space-y-6">
          {/* Step 1: Tenant */}
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-white">
              <Building2 className="w-5 h-5 text-[#D50C2D]" />
              1. Tenant auswaehlen
            </h3>
            <div className="relative">
              <select
                data-testid="tenant-select"
                value={selectedTenant}
                onChange={e => { setSelectedTenant(e.target.value); setSelectedDevices([]); }}
                className="w-full bg-[#2A2A2A] text-white border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#D50C2D]"
              >
                <option value="">Tenant waehlen...</option>
                {tenants.map(t => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888] pointer-events-none" />
            </div>
          </div>

          {/* Step 2: Devices */}
          {selectedTenant && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold text-white">
                  <Monitor className="w-5 h-5 text-[#D50C2D]" />
                  2. Geraete auswaehlen
                </h3>
                <div className="text-xs text-[#888]">
                  {devices.length} Geraete{' '}
                  <span className="text-green-400">{onlineCount} online</span>{' '}
                  <span className="text-orange-400">{outdatedDevices.length} veraltet</span>
                </div>
              </div>

              <div className="flex gap-2 mb-2">
                <button
                  data-testid="select-outdated-btn"
                  onClick={selectAllOutdated}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600/20 text-orange-400 border border-orange-600/30 hover:bg-orange-600/30 transition-colors"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Alle veralteten ({outdatedDevices.length})
                </button>
                <button
                  data-testid="select-all-btn"
                  onClick={selectAll}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#2A2A2A] text-[#AAA] border border-[#3A3A3A] hover:bg-[#333] transition-colors"
                >
                  Alle auswaehlen
                </button>
              </div>

              {/* Device Table */}
              <div data-testid="device-table" className="bg-[#252525] rounded-xl border border-[#3A3A3A] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3A3A3A]">
                      <th className="w-10 px-4 py-3" />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#888] uppercase tracking-wider">Geraet</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#888] uppercase tracking-wider">Version</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#888] uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-[#888] uppercase tracking-wider">TV / RD / AD</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#888] uppercase tracking-wider">Install</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#888] uppercase tracking-wider">Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(device => {
                      const isCurrent = semverGte(device.agent_version, serverVersion);
                      const isSelected = selectedDevices.includes(device.id);
                      return (
                        <tr key={device.id} data-testid={`device-row-${device.computer_name}`} className="border-b border-[#3A3A3A]/50 hover:bg-[#2A2A2A] transition-colors">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              data-testid={`device-checkbox-${device.computer_name}`}
                              checked={isSelected}
                              onChange={() => toggleDevice(device.id)}
                              className="w-4 h-4 rounded border-[#3A3A3A] bg-[#1E1E1E] accent-[#D50C2D]"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${device.is_online ? 'bg-green-500' : 'bg-gray-600'}`} />
                              <span className="text-sm font-medium text-white">{device.computer_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-mono" style={{ color: getVersionColor(device.agent_version) }}>
                              {device.agent_version}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${device.is_online ? 'text-white' : 'text-gray-500'}`}>
                              {device.is_online ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <ToolLed running={device.tv_running} />
                              <ToolLed running={device.rd_running} />
                              <ToolLed running={device.ad_running} />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <InstallLed status={device.install_status} timestamp={device.install_at} />
                          </td>
                          <td className="px-4 py-3">
                            {isCurrent ? (
                              <span data-testid={`status-current-${device.computer_name}`} className="flex items-center gap-1 text-sm text-green-400">
                                <Check className="w-4 h-4" /> Aktuell
                              </span>
                            ) : (
                              <span data-testid={`status-outdated-${device.computer_name}`} className="flex items-center gap-1 text-sm text-orange-400">
                                <RefreshCw className="w-3.5 h-3.5" /> Update verfuegbar
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {devices.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-[#888]">
                          {loading ? 'Lade Geraete...' : 'Keine Geraete gefunden'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Selection Footer */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888]">
                  {selectedDevices.length} von {devices.length} Geraeten ausgewaehlt
                </span>
                <button
                  data-testid="start-rollout-btn"
                  onClick={startRollout}
                  disabled={selectedDevices.length === 0 || rolloutLoading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#D50C2D] hover:bg-[#B00A25] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Rocket className="w-4 h-4" />
                  Update starten ({selectedDevices.length})
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Content: History */}
      {activeTab === 'history' && (
        <div data-testid="history-tab" className="space-y-4">
          {rolloutHistory.length === 0 ? (
            <div className="text-center py-12 text-[#888]">Noch keine Rollout-Historie vorhanden</div>
          ) : (
            rolloutHistory.map(h => (
              <div key={h.id} className="bg-[#252525] rounded-xl p-4 border border-[#3A3A3A]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">Zielversion: {h.target_version}</span>
                  <span className="text-xs text-[#888]">{new Date(h.created_at).toLocaleString('de-DE')}</span>
                </div>
                <div className="flex gap-4 text-xs text-[#888]">
                  <span>Gesamt: {h.total}</span>
                  <span className="text-green-400">Versendet: {h.dispatched}</span>
                  <span className="text-orange-400">Uebersprungen: {h.skipped}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab Content: Schedule (Placeholder) */}
      {activeTab === 'schedule' && (
        <div data-testid="schedule-tab" className="text-center py-12 text-[#888]">
          <CalendarDays className="w-10 h-10 mx-auto mb-3 text-[#555]" />
          <p className="text-sm">Zeitplan-Funktion wird in einer zukuenftigen Version verfuegbar sein.</p>
        </div>
      )}

    </div>
  );
}
